import {useLocation, useNavigate} from "react-router-dom";
import {Center, CloseButton, Container, Group, Loader, Paper, Space, Title} from "@mantine/core";
import {
  adjustFromTimeZoneSafely,
  adjustToTimeZone,
  EntityIdentifier,
  getObjectId,
  ProductionVolume,
  ProductionVolumeEntity,
  productionVolumeSelector,
  Quantity,
  useGetProductionVolumeQuery
} from "@ots-frontend/ots-data";
import {useTranslation} from "react-i18next";
import {useCallback, useMemo, useState} from "react";
import {skipToken} from "@reduxjs/toolkit/query";
import {
  ProductionVolumeForm,
  ProductionVolumeFormValues
} from "../../components/form/ProductionVolumeForm/ProductionVolumeForm";
import {ThemedPage} from "../../components/page/ThemedPage/ThemedPage";
import {useProductionVolumeMutation} from "../../hooks/useProductionVolumeMutation";
import {useApp} from "../../providers/RegionProvider/RegionProvider";
import {ProtectedPage} from "../../components/page/ProtectedPage/ProtectedPage";

type Props = {
  id?: string;
  copy?: boolean;
  formDefaults?: Partial<ProductionVolumeFormValues>
}

export const ProductionVolumeFormPageRoute = () => {
  const {state} = useLocation();
  const id = state?.id;
  const copy = state?.copy;
  const formDefaults = state?.formDefaults;
  return (
    <ProductionVolumeFormPage
      id={id}
      copy={copy}
      formDefaults={formDefaults}
    />
  );
};

export const toFormDefaultsFromProductionVolume = (productionVolume: ProductionVolumeEntity | undefined): Partial<ProductionVolumeFormValues> => {
  let object: EntityIdentifier | undefined = undefined
  let quantity: Quantity | undefined = undefined;

  if (productionVolume) {
    if (productionVolume.objectId && productionVolume.objectType) {
      object = {
        id: productionVolume.objectId,
        type: productionVolume.objectType,
      }
    }
    if (typeof productionVolume.quantity !== 'undefined' && productionVolume.unitOfMeasure) {
      quantity = {
        number: productionVolume.quantity,
        units: productionVolume.unitOfMeasure
      };
    }
  }

  return {
    timeGroup: productionVolume?.timeGroup,
    date: adjustFromTimeZoneSafely(productionVolume?.dttm),
    object: object,
    quantity: quantity,
    freightType: productionVolume?.freightTypeId,
  };
};

export const ProductionVolumeFormPage = (props: Props) => {
  const {id, copy = false, formDefaults} = props;

  const {regionId} = useApp()
  const navigate = useNavigate();
  const {t} = useTranslation(["common"]);

  const [access, setAccess] = useState(false);

  const selectProductionVolume = useMemo(() => {
    return productionVolumeSelector()
  }, [])

  const {currProductionVolume, isLoading} = useGetProductionVolumeQuery(access ? (id ?? skipToken) : skipToken, {
    selectFromResult: (result) => ({
      currProductionVolume: selectProductionVolume(result),
      isLoading: result.isLoading
    })
  });

  const _formDefaults: Partial<ProductionVolumeFormValues> = useMemo(() => {
    return formDefaults ? formDefaults : toFormDefaultsFromProductionVolume(currProductionVolume);
  }, [currProductionVolume, formDefaults]);

  const isEdit = useMemo(() => {
    return currProductionVolume && !copy
  }, [currProductionVolume, copy])

  const title: string = useMemo(() => {
    if (isEdit) {
      return t("page.entity.productionVolume.form.title.edit", "Edit Production Volume", {ns: "common"});
    } else {
      return t("page.entity.productionVolume.form.title.new", "New Production Volume", {ns: "common"});
    }
  }, [isEdit, t]);

  const {
    createProductionVolume,
    updateProductionVolume,
    isCreating,
    isUpdating,
  } = useProductionVolumeMutation()

  const onProductionVolumeSubmit = useCallback((formValues: ProductionVolumeFormValues) => {
    const patch: Partial<ProductionVolume> = {
      timeGroup: formValues.timeGroup.code,
      dttm: adjustToTimeZone(formValues.date).getTime(),
      objectId: formValues.object.id,
      objectType: formValues.object.type,
      quantity: formValues.quantity.number,
      unitOfMeasure: formValues.quantity.units,
      freightTypeId: getObjectId(formValues.freightType),
      regionId: regionId,
    }

    if (isEdit) {
      const productionVolume: Partial<ProductionVolume> = {
        ...currProductionVolume.asTypeValue(),
        ...patch,
      };
      updateProductionVolume(productionVolume, () => navigate(-1))
    } else {
      const productionVolume: Partial<ProductionVolume> = {
        ...patch,
      };
      createProductionVolume(productionVolume, () => navigate(-1))
    }
  }, [regionId, currProductionVolume, isEdit, updateProductionVolume, navigate, createProductionVolume]);

  return (
    <ProtectedPage
      permissions={[isEdit ? "productionvolume_edit" : "productionvolume_create"]}
      isLoading={isLoading}
      onAccess={setAccess}>
      {(classes) => (
        <Container className={classes.container} fluid={true} py="md">
          <Center>
            {isLoading && <Loader/>}
            {!isLoading && (
              <Paper miw={500} shadow="sm" p="md" py="md">
                <Group position="apart">
                  <Title align="center">
                    {title}
                  </Title>
                  <CloseButton title="Close" size="xl" iconSize={20} onClick={() => navigate(-1)}/>
                </Group>
                <Space h="md"/>
                <ProductionVolumeForm
                  options={{
                    regionId: currProductionVolume?.regionId ?? regionId,
                  }}
                  defaultValues={_formDefaults}
                  submitting={isCreating || isUpdating}
                  onSubmit={onProductionVolumeSubmit}/>
              </Paper>
            )}
          </Center>
        </Container>
      )}
    </ProtectedPage>
  );
};

